import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { MedConsultAssessmentAdvice } from '@common/customControls/bariatrics';
import { getTextAreaConditionalSchema } from '@common/validation/utils/getTextAreaConditionalSchema';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type {
    AdviesAan,
    ConclusieAanvullend,
    ConclusieAanvullendReadKey,
    ConclusieAanvullendReadLabel,
    IndicatieVerwijzing,
    IndicatieVerwijzingReadKey,
    IndicatieVerwijzingSectionTitle,
    IndicatieVoorVerwijzingReadLabel,
} from './../../utils';
import { conclusieAanvullendSectionItems, indicatieVerwijzingReadSection } from './../../utils';

export type BariPreMedConsultAssessmentData = AdviesAan &
    ConclusieAanvullend &
    IndicatieVerwijzing & {
        goedkeuring_chirurg?: boolean;
        inclusie_in_zorgpad: MedConsultAssessmentAdvice | null;
        toelichting: string | null;
    };

export type BariPreMedConsultAssessmentReadKey =
    | keyof BariPreMedConsultAssessmentData
    | ConclusieAanvullendReadKey
    | IndicatieVerwijzingReadKey;
export type BariPreMedConsultAssessmentReadLabel =
    | 'Advies aan patiënt'
    | 'Advies aan huisarts'
    | 'Advies aan chirurg'
    | ConclusieAanvullendReadLabel
    | 'Inclusie in zorgpad'
    | IndicatieVoorVerwijzingReadLabel
    | 'Toelichting';
export type BariPreMedConsultAssessmentReadSectionTitle = 'Conclusie' | IndicatieVerwijzingSectionTitle;

export const bariPreMedConsultAssessmentReadSchema: TypedReadSchema<
    BariPreMedConsultAssessmentReadKey,
    BariPreMedConsultAssessmentReadLabel,
    BariPreMedConsultAssessmentReadSectionTitle
>[] = [
    {
        items: [
            { key: 'inclusie_in_zorgpad', label: 'Inclusie in zorgpad' },
            { key: 'toelichting', label: 'Toelichting' },
            { key: 'advies_aan_patient', label: 'Advies aan patiënt' },
            { key: 'advies_aan_huisarts', label: 'Advies aan huisarts' },
            { key: 'advies_aan_chirurg', label: 'Advies aan chirurg' },
            ...conclusieAanvullendSectionItems,
        ],
        sectionTitle: 'Conclusie',
    },
    { ...indicatieVerwijzingReadSection },
];

// goedkeuring_chirurg was deliberately left out because we don't want that value to be saved in the draft
const bariPreMedConsultAssessmentDraft: TypedSchema<Omit<BariPreMedConsultAssessmentData, 'goedkeuring_chirurg'>> = {
    advies_aan_chirurg: getTextAreaStringSchema(true),
    advies_aan_huisarts: getTextAreaStringSchema(true),
    advies_aan_patient: getTextAreaStringSchema(true),
    conclusie_aanvullend_diabetes_mellitus: Koi.boolean(),
    conclusie_aanvullend_hydrochloorthiazide: Koi.boolean(),
    conclusie_aanvullend_psychofarmaca: Koi.boolean(),
    conclusie_aanvullend_roken: Koi.boolean(),
    inclusie_in_zorgpad: Koi.string()
        .valid(...Object.values(MedConsultAssessmentAdvice))
        .allow(null),
    indicatie_voor_verwijzing_diabetes: Koi.boolean(),
    indicatie_voor_verwijzing_hartfalen: Koi.boolean(),
    indicatie_voor_verwijzing_hypertensie: Koi.boolean(),
    indicatie_voor_verwijzing_medicatie: Koi.boolean(),
    indicatie_voor_verwijzing_nefrologie: Koi.boolean(),
    indicatie_voor_verwijzing_obesitas_specifiek: Koi.boolean(),
    indicatie_voor_verwijzing_osteoporose: Koi.boolean(),
    indicatie_voor_verwijzing_overig: Koi.boolean(),
    indicatie_voor_verwijzing_overig_toelichting: getTextAreaConditionalSchema(
        'indicatie_voor_verwijzing_overig',
        true
    ),
    toelichting: getTextAreaStringSchema(true),
};

const bariPreMedConsultAssessmentFinal: TypedSchema<BariPreMedConsultAssessmentData> = {
    ...bariPreMedConsultAssessmentDraft,
    goedkeuring_chirurg: Koi.boolean().valid(true),
    inclusie_in_zorgpad: Koi.string().valid(...Object.values(MedConsultAssessmentAdvice)),
    indicatie_voor_verwijzing_overig_toelichting: getTextAreaConditionalSchema('indicatie_voor_verwijzing_overig'),
    toelichting: getTextAreaStringSchema(),
};

export const bariPreMedConsultAssessmentDraftSchema = Koi.object(bariPreMedConsultAssessmentDraft);
export const bariPreMedConsultAssessmentFinalSchema = Koi.object(bariPreMedConsultAssessmentFinal);
